import { useEffect, useRef, useState } from "react";
import { DatabaseBackup, Download, Upload, Loader2, History, FileJson, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

const COLL_LABEL = {
  businesses: "Bisnis",
  templates: "Template Konten",
  content_ideas: "Ide Tersimpan",
  scheduled_posts: "Jadwal Posting",
  app_settings: "Pengaturan",
  users: "Akun User",
};

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export default function BackupRestore() {
  const [history, setHistory] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadingEnv, setDownloadingEnv] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const downloadFile = async (path, fallbackName) => {
    const r = await api.get(path, { responseType: "blob" });
    const dispo = r.headers["content-disposition"] || "";
    const match = dispo.match(/filename="?([^";]+)"?/);
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = match ? match[1] : fallbackName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadEnv = async () => {
    if (!window.confirm("File .env berisi SEMUA kredensial sensitif (JWT secret, kunci enkripsi, kunci LLM). Unduh hanya di perangkat pribadi yang aman. Lanjutkan?")) return;
    setDownloadingEnv(true);
    try {
      await downloadFile("/backup/env-download", "backend.env");
      toast.success("File .env berhasil diunduh");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloadingEnv(false);
    }
  };

  const loadHistory = async () => {
    try { setHistory((await api.get("/backup/history")).data || []); } catch { /* abaikan */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const backup = async () => {
    setDownloading(true);
    try {
      await downloadFile("/backup/download", `backup-contentchatai-${Date.now()}.json`);
      toast.success("Backup berhasil diunduh");
      loadHistory();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const restore = async () => {
    if (!file) { toast.error("Pilih file backup (.json) dulu"); return; }
    if (!window.confirm("Restore akan MENIMPA data yang memiliki ID sama dengan isi file backup. Data baru yang tidak ada di backup tetap aman. Lanjutkan?")) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = Object.entries(r.data.restored || {}).map(([k, v]) => `${COLL_LABEL[k] || k}: ${v}`);
      toast.success(`Restore selesai — ${parts.join(" · ")}`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
      {/* Backup */}
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 h-fit">
        <div className="flex items-center gap-2 mb-1">
          <DatabaseBackup size={18} className="text-[#2563EB]" />
          <h2 className="font-heading font-bold">Backup Data Internal</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Unduh seluruh data internal aplikasi dalam satu file JSON: template konten, ide tersimpan, jadwal posting, konfigurasi bisnis, dan pengaturan. Bisa dilakukan kapan saja.
        </p>
        <Button onClick={backup} disabled={downloading} data-testid="backup-download-button" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
          {downloading ? <Loader2 size={16} className="animate-spin mr-1" /> : <Download size={16} className="mr-1" />}
          {downloading ? "Menyiapkan backup..." : "Unduh Backup JSON"}
        </Button>
        <div className="mt-5 pt-4 border-t border-[#E2E8F0]">
          <p className="text-xs text-slate-500 mb-3">Butuh file environment untuk deploy manual? Unduh <span className="font-mono-code text-slate-700">.env</span> backend (berisi kredensial sensitif).</p>
          <Button onClick={downloadEnv} disabled={downloadingEnv} variant="outline" data-testid="env-download-button" className="w-full border-[#F43F5E]/40 bg-[#F43F5E]/10 hover:bg-[#F43F5E]/20 text-[#E11D48] rounded-xl text-xs">
            {downloadingEnv ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileJson size={14} className="mr-1" />}
            Unduh File .env (Sensitif)
          </Button>
        </div>
      </div>

      {/* Restore */}
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 h-fit">
        <div className="flex items-center gap-2 mb-1">
          <Upload size={18} className="text-[#B45309]" />
          <h2 className="font-heading font-bold">Restore dari File</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Unggah file backup JSON untuk memulihkan data. Data dengan ID yang sama akan ditimpa.
        </p>
        <div className="flex items-start gap-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-3 mb-4">
          <ShieldAlert size={15} className="text-[#B45309] mt-0.5 shrink-0" />
          <p className="text-[11px] text-slate-600 leading-snug">File backup memuat konfigurasi sensitif (token Fonnte, kredensial database, password admin). Simpan file di tempat aman.</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="restore-file-input"
          className="block w-full text-xs text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-[#F1F5F9] file:text-slate-800 hover:file:bg-[#E2E8F0] file:cursor-pointer mb-4"
        />
        {file && (
          <p className="text-xs text-slate-600 mb-3 flex items-center gap-1.5" data-testid="restore-file-info">
            <FileJson size={13} className="text-[#B45309]" /> {file.name} · {fmtSize(file.size)}
          </p>
        )}
        <Button onClick={restore} disabled={restoring || !file} data-testid="restore-upload-button" className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0F172A] rounded-xl font-semibold">
          {restoring ? <Loader2 size={16} className="animate-spin mr-1" /> : <Upload size={16} className="mr-1" />}
          {restoring ? "Memulihkan..." : "Restore Sekarang"}
        </Button>
      </div>

      {/* Riwayat */}
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 lg:col-span-2" data-testid="backup-history-card">
        <div className="flex items-center gap-2 mb-1">
          <History size={18} className="text-[#059669]" />
          <h2 className="font-heading font-bold">Riwayat Backup</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">10 backup terakhir yang dibuat dari aplikasi ini.</p>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="backup-history-empty">Belum ada backup. Klik "Unduh Backup JSON" untuk membuat yang pertama.</p>
        ) : (
          <div className="space-y-2" data-testid="backup-history-list">
            {history.map((h) => (
              <div key={h.id} data-testid={`backup-history-item-${h.id}`} className="flex flex-wrap items-center gap-3 bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl px-4 py-3">
                <FileJson size={16} className="text-[#2563EB] shrink-0" />
                <div className="min-w-0 w-full sm:w-auto flex-1 sm:min-w-[180px]">
                  <p className="text-sm font-medium text-slate-800">{new Date(h.created_at).toLocaleString("id-ID")}</p>
                  <p className="text-[11px] text-slate-500">
                    {Object.entries(h.counts || {}).map(([k, v]) => `${COLL_LABEL[k] || k} ${v}`).join(" · ")}
                  </p>
                </div>
                <span className="text-xs font-mono-code text-slate-600 shrink-0" data-testid={`backup-size-${h.id}`}>{fmtSize(h.size_bytes)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
